import { colorToHex } from "@excalidraw/common";
import { MORPH_SHAPE_LABELS } from "@excalidraw/element";
import { ColorPicker } from "@excalidraw/excalidraw/components/ColorPicker/ColorPicker";
import { useState } from "react";

import type { ExcalidrawElement } from "@excalidraw/element/types";

import type { UIAppState } from "@excalidraw/excalidraw/types";

import {
  animationPresets,
  EASING_OPTIONS,
  supportsCustomPath,
} from "../animation/presets";
import { svgPathToOutline } from "../animation/presets/morph";
import {
  createSpring,
  SPRING_DEFAULTS,
  SPRING_RANGES,
} from "../animation/spring";
import { EasingEditor, useEasingEditor } from "../animation/easing-editor";

import { KeyframeEditor } from "./KeyframeEditor";

import type { PointerEvent as ReactPointerEvent } from "react";

import type { ElementAnimation } from "../animation/types";

// order matches SLIDE_VECTORS; edge-based presets cap at 4 via schema max
const DIRECTION_LABELS = [
  "Left",
  "Right",
  "Up",
  "Down",
  "Up left",
  "Up right",
  "Down left",
  "Down right",
];

// number inputs accept typed values outside min/max (the attributes only
// constrain the spinner) — clamp when the field commits (blur)
const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, Number.isFinite(value) ? value : min));

/** Settings form for one animation clip (duration / delay / repeat / easing /
 * spring / preset params). Rendered inside the expanding bottom panel. */
export const ClipSettings = ({
  clip,
  element,
  elements,
  appState,
  onUpdate,
  onRemove,
  pathTools,
  onDrawMorphTarget,
  onPickAnchor,
}: {
  clip: ElementAnimation;
  /** the element this clip belongs to — needed to size the dynamic
   * per-stop gradient color UI and to seed the real ColorPicker */
  element: ExcalidrawElement;
  elements: readonly ExcalidrawElement[];
  appState: UIAppState;
  onUpdate: (updates: Partial<ElementAnimation>) => void;
  onRemove: () => void;
  /** followPath clips only: current binding label + "draw a path" trigger
   * (provided by the dock, which owns the excalidraw API). */
  pathTools?: { boundTo: string | null; onDrawPath: () => void };
  /** morph clips only: draw the morph target on the main canvas
   * (provided by the dock, which owns the excalidraw API). */
  onDrawMorphTarget?: () => void;
  /** pivot-based clips (rotate/scale) only: pick the anchor point by
   * clicking the canvas (provided by the dock, which owns the API). */
  onPickAnchor?: () => void;
}) => {
  const [easingEditorOpen, setEasingEditorOpen] = useState(false);
  const [drawFrameOpen, setDrawFrameOpen] = useState(false);
  const [curvesOpen, setCurvesOpen] = useState(false);
  // ColorPicker's popover visibility is gated on a SHARED appState.openPopup
  // slot keyed only by picker "type" ("elementStroke"/"elementBackground") —
  // fine for the one-picker-per-panel case it was built for, but this panel
  // can render many same-type pickers at once (7 rainbow stops, 4 gradient
  // stops). Track which param key owns the popup locally and synthesize a
  // per-field appState, the same override DesignPanel uses for its text-color
  // picker sharing the "elementStroke" slot with the shape stroke picker.
  const [openColorParam, setOpenColorParam] = useState<string | null>(null);
  const { customEasings, saveCustomEasing, deleteCustomEasing } =
    useEasingEditor();

  const handleSaveCustomEasing = (easing: string, name: string) => {
    saveCustomEasing(easing, name);
    // Auto-select the newly created easing
    onUpdate({ easing, spring: undefined });
    setEasingEditorOpen(false);
  };

  // custom easings live in per-browser localStorage but the clip's easing
  // travels with the scene — an unknown value needs its own option or the
  // select would silently display "none" while the clip plays the custom curve
  const easingValue = clip.spring ? "spring" : (clip.easing as string);
  // sampled preview of the custom morph path (null = invalid/unsupported)
  const customOutline = clip.customPath
    ? svgPathToOutline(clip.customPath)
    : null;
  const isKnownEasing =
    clip.spring ||
    (EASING_OPTIONS as readonly string[]).includes(easingValue) ||
    customEasings.some((e) => e.value === easingValue);

  /** Shared render for every numeric-hex color param (`color`,
   * `gradientColorN`, `rainbowColorN`) — a real palette/eyedropper picker
   * instead of a bare native swatch, keyed into `openColorParam` so only one
   * of the (possibly many, same-`type`) pickers on this panel pops open. */
  const renderColorField = (
    paramKey: string,
    label: string,
    type: "elementStroke" | "elementBackground",
    fallback: number,
  ) => {
    const hexValue = clip.params[paramKey] ?? fallback;
    return (
      <ColorPicker
        type={type}
        label={label}
        color={`#${Math.max(0, Math.min(0xffffff, Math.round(hexValue)))
          .toString(16)
          .padStart(6, "0")}`}
        onChange={(color) => {
          // the picker emits #rgb / #rrggbbaa / "red" / rgb(...) — normalize
          // before parsing. Alpha is dropped: params are 24-bit ints by design.
          const hex = colorToHex(color);
          if (hex) {
            onUpdate({ params: { [paramKey]: parseInt(hex.slice(1, 7), 16) } });
          }
        }}
        elements={elements}
        appState={{
          ...appState,
          openPopup: openColorParam === paramKey ? type : null,
        }}
        updateData={(data) =>
          setOpenColorParam(data?.openPopup ? paramKey : null)
        }
      />
    );
  };

  return (
    <>
      <label>
        Duration (ms)
        <input
          type="number"
          min={100}
          max={60000}
          step={100}
          disabled={!!clip.spring}
          value={
            clip.spring ? createSpring(clip.spring).durationMs : clip.duration
          }
          title={
            clip.spring ? "Derived from the spring's settling time" : undefined
          }
          onChange={(e) => {
            // don't commit a cleared field mid-edit (Number("") is 0); blur
            // clamps whatever is left
            const value = Number(e.target.value);
            if (e.target.value !== "" && Number.isFinite(value)) {
              onUpdate({ duration: value });
            }
          }}
          onBlur={(e) =>
            onUpdate({ duration: clamp(Number(e.target.value), 100, 60000) })
          }
        />
      </label>
      <label>
        Delay (ms)
        <input
          type="number"
          min={0}
          max={60000}
          step={100}
          value={clip.delay}
          onChange={(e) => {
            const value = Number(e.target.value);
            if (e.target.value !== "" && Number.isFinite(value)) {
              onUpdate({ delay: value });
            }
          }}
          onBlur={(e) =>
            onUpdate({ delay: clamp(Number(e.target.value), 0, 60000) })
          }
        />
      </label>
      <label>
        Repeat (-1 = loop)
        <input
          type="number"
          min={-1}
          max={20}
          value={clip.repeat}
          onChange={(e) => {
            const value = Number(e.target.value);
            if (e.target.value !== "" && Number.isFinite(value)) {
              onUpdate({ repeat: value });
            }
          }}
          onBlur={(e) =>
            onUpdate({
              repeat: clamp(Math.round(Number(e.target.value)), -1, 20),
            })
          }
        />
      </label>
      {clip.preset === "keyframed" && (
        <label>
          Keyframe curves
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              className="clip-settings__editor-btn"
              onClick={() => setCurvesOpen(!curvesOpen)}
            >
              {curvesOpen ? "Hide curves" : "Edit curves"}
            </button>
            <span className="clip-settings__path-status">
              {Object.keys(clip.tracks ?? {}).length} channel
              {Object.keys(clip.tracks ?? {}).length === 1 ? "" : "s"}
            </span>
          </div>
        </label>
      )}
      {clip.preset === "keyframed" && curvesOpen && (
        <KeyframeEditor
          tracks={clip.tracks ?? {}}
          durationMs={clip.duration}
          onChange={(tracks) => onUpdate({ tracks })}
          onClose={() => setCurvesOpen(false)}
        />
      )}
      <label className="animation-panel__inline">
        <input
          type="checkbox"
          checked={clip.yoyo === true}
          onChange={(e) => onUpdate({ yoyo: e.target.checked || undefined })}
        />
        Yoyo (reverse on alternate repeats)
      </label>
      <label>
        Repeat gap (ms)
        <input
          type="number"
          min={0}
          max={60000}
          step={100}
          value={clip.repeatDelayMs ?? 0}
          onChange={(e) => {
            const value = Number(e.target.value);
            if (e.target.value !== "" && Number.isFinite(value)) {
              onUpdate({ repeatDelayMs: value || undefined });
            }
          }}
          onBlur={(e) =>
            onUpdate({
              repeatDelayMs:
                clamp(Number(e.target.value), 0, 60000) || undefined,
            })
          }
        />
      </label>
      <label>
        End padding (ms)
        <input
          type="number"
          min={0}
          max={60000}
          step={100}
          value={clip.endDelayMs ?? 0}
          onChange={(e) => {
            const value = Number(e.target.value);
            if (e.target.value !== "" && Number.isFinite(value)) {
              onUpdate({ endDelayMs: value || undefined });
            }
          }}
          onBlur={(e) =>
            onUpdate({
              endDelayMs: clamp(Number(e.target.value), 0, 60000) || undefined,
            })
          }
        />
      </label>
      <label>
        Variation ({Math.round((clip.variation?.jitter ?? 0) * 100)}%)
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            style={{ flex: 1 }}
            value={Math.round((clip.variation?.jitter ?? 0) * 100)}
            title="Deterministic per-element randomness on timing and params — makes copies of one preset feel hand-made"
            onChange={(e) => {
              const jitter = Number(e.target.value) / 100;
              onUpdate({
                variation:
                  jitter <= 0
                    ? undefined
                    : { seed: clip.variation?.seed ?? 1, jitter },
              });
            }}
          />
          {clip.variation && (
            <button
              className="clip-settings__editor-btn"
              title="Re-roll the variation seed"
              onClick={() =>
                onUpdate({
                  variation: {
                    ...clip.variation!,
                    // authoring-time randomness only — the chosen seed is
                    // persisted, so compile/export stay deterministic
                    seed: Math.floor(Math.random() * 2 ** 31),
                  },
                })
              }
            >
              ↻
            </button>
          )}
        </div>
      </label>
      <label>
        Easing
        <div style={{ display: "flex", gap: "8px" }}>
          <select
            value={easingValue}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "spring") {
                onUpdate({ spring: { ...SPRING_DEFAULTS } });
              } else {
                // custom-easing options carry their cubic-bezier value directly
                onUpdate({ easing: value, spring: undefined });
              }
            }}
            style={{ flex: 1 }}
          >
            {!isKnownEasing && (
              <option value={easingValue}>custom ({easingValue})</option>
            )}
            {EASING_OPTIONS.map((ease) => (
              <option key={ease} value={ease}>
                {ease}
              </option>
            ))}
            {customEasings.length > 0 && (
              <optgroup label="Custom Easings">
                {customEasings.map((e) => (
                  <option key={e.id} value={e.value}>
                    {e.name}
                  </option>
                ))}
              </optgroup>
            )}
            <option value="spring">spring (physics)</option>
          </select>
          <button
            className="clip-settings__editor-btn"
            onClick={() => setEasingEditorOpen(true)}
            title="Open easing curve editor"
          >
            Editor
          </button>
          {(() => {
            // the selected easing is a saved custom curve → offer removal
            // (the clip keeps its curve; it just leaves the dropdown)
            const selected = customEasings.find((e) => e.value === easingValue);
            return selected ? (
              <button
                className="clip-settings__editor-btn"
                title={`Remove "${selected.name}" from your saved easings`}
                onClick={() => deleteCustomEasing(selected.id)}
              >
                ×
              </button>
            ) : null;
          })()}
        </div>
      </label>
      {easingEditorOpen && (
        <EasingEditor
          initialEasing={clip.spring ? undefined : (clip.easing as string)}
          onSave={handleSaveCustomEasing}
          onClose={() => setEasingEditorOpen(false)}
        />
      )}
      {clip.spring &&
        (Object.keys(SPRING_RANGES) as (keyof typeof SPRING_RANGES)[]).map(
          (key) => (
            <label key={key}>
              {key[0].toUpperCase() + key.slice(1)}
              <input
                type="number"
                min={SPRING_RANGES[key].min}
                max={SPRING_RANGES[key].max}
                step={SPRING_RANGES[key].step}
                value={clip.spring![key]}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  if (e.target.value !== "" && Number.isFinite(value)) {
                    onUpdate({
                      spring: { ...clip.spring!, [key]: value },
                    });
                  }
                }}
                onBlur={(e) =>
                  onUpdate({
                    spring: {
                      ...clip.spring!,
                      [key]: clamp(
                        Number(e.target.value),
                        SPRING_RANGES[key].min,
                        SPRING_RANGES[key].max,
                      ),
                    },
                  })
                }
              />
            </label>
          ),
        )}
      {pathTools && (
        <label>
          Motion path
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <span className="clip-settings__path-status">
              {pathTools.boundTo ?? "auto (nearest line)"}
            </span>
            <button onClick={pathTools.onDrawPath}>Draw path</button>
          </div>
        </label>
      )}
      {onPickAnchor && (
        <label>
          Anchor
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <span className="clip-settings__path-status">
              {`${clip.params.pivotX ?? 0.5}, ${clip.params.pivotY ?? 0.5}`}
            </span>
            <button onClick={onPickAnchor}>Pick on canvas</button>
          </div>
        </label>
      )}
      {animationPresets[clip.preset]?.paramsSchema.map((param) =>
        param.key === "direction" ? (
          <label key={param.key}>
            {param.label}
            <select
              value={clip.params[param.key] ?? 1}
              onChange={(e) =>
                onUpdate({ params: { [param.key]: Number(e.target.value) } })
              }
            >
              {DIRECTION_LABELS.slice(0, param.max + 1).map((label, i) => (
                <option key={label} value={i}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        ) : param.key === "shape" ? (
          <label key={param.key}>
            {param.label}
            <div style={{ display: "flex", gap: "8px" }}>
              <select
                style={{ flex: 1 }}
                value={clip.customPath ? "custom" : clip.params[param.key] ?? 0}
                onChange={(e) => {
                  if (e.target.value === "custom") {
                    setDrawFrameOpen(true);
                  } else {
                    // a drawn shape overrides the dropdown — picking a
                    // built-in shape discards it
                    onUpdate({
                      params: { [param.key]: Number(e.target.value) },
                      customPath: undefined,
                    });
                    setDrawFrameOpen(false);
                  }
                }}
              >
                {MORPH_SHAPE_LABELS.map((label, i) => (
                  <option key={label} value={i}>
                    {label}
                  </option>
                ))}
                <option value="custom">Custom — draw it</option>
              </select>
              {clip.customPath && (
                <button onClick={() => setDrawFrameOpen(true)}>Edit</button>
              )}
            </div>
          </label>
        ) : param.options ? (
          // enum param: labeled dropdown (option index = param value)
          <label key={param.key}>
            {param.label}
            <select
              value={clamp(
                Math.round(clip.params[param.key] ?? param.min),
                param.min,
                param.max,
              )}
              onChange={(e) =>
                onUpdate({ params: { [param.key]: Number(e.target.value) } })
              }
            >
              {param.options.map((label, i) => (
                <option key={label} value={i}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        ) : /^gradientColor\d+$/.test(param.key) ? (
          (() => {
            const idx = Number(param.key.replace("gradientColor", ""));
            const stopCount = element.strokeGradient?.stops.length ?? 0;
            if (idx >= stopCount) {
              return null;
            }
            return (
              <label key={param.key}>
                {param.label}
                {renderColorField(
                  param.key,
                  param.label,
                  "elementStroke",
                  0xffffff,
                )}
              </label>
            );
          })()
        ) : /^rainbowColor\d+$/.test(param.key) ? (
          <label key={param.key}>
            {param.label}
            {renderColorField(param.key, param.label, "elementStroke", 0)}
          </label>
        ) : param.key === "color" ? (
          <label key={param.key}>
            {param.label}
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              {renderColorField(
                param.key,
                param.label,
                clip.preset === "fillTint"
                  ? "elementBackground"
                  : "elementStroke",
                0,
              )}
              {/* min -1 marks an "inherit element color" sentinel */}
              {param.min === -1 &&
                ((clip.params[param.key] ?? -1) < 0 ? (
                  <span className="clip-settings__path-status">
                    auto (element color)
                  </span>
                ) : (
                  <button
                    title="Inherit the element's color"
                    onClick={() => onUpdate({ params: { [param.key]: -1 } })}
                  >
                    Auto
                  </button>
                ))}
            </div>
          </label>
        ) : param.min === 0 && param.max === 1 && param.step === 1 ? (
          // 0/1 params are toggles (orient to path, snap, travel, …)
          <label key={param.key} className="animation-panel__inline">
            <input
              type="checkbox"
              checked={(clip.params[param.key] ?? 0) >= 1}
              onChange={(e) =>
                onUpdate({ params: { [param.key]: e.target.checked ? 1 : 0 } })
              }
            />
            {param.label}
          </label>
        ) : (
          // numeric param: slider for feel, number field for precision —
          // the slider makes the designed range visible without reading docs
          <label key={param.key}>
            {param.label}
            <div className="clip-settings__param-row">
              <input
                type="range"
                min={param.min}
                max={param.max}
                step={param.step}
                value={clamp(clip.params[param.key] ?? 0, param.min, param.max)}
                onChange={(e) =>
                  onUpdate({ params: { [param.key]: Number(e.target.value) } })
                }
              />
              <input
                type="number"
                min={param.min}
                max={param.max}
                step={param.step}
                value={clip.params[param.key] ?? 0}
                onChange={(e) => {
                  // guard NaN (a NaN param persists into customData and gets
                  // the whole clip dropped as malformed on reload) and the
                  // cleared field — Number("") is 0, which would instantly
                  // commit 0 mid-edit; blur re-clamps whatever is left
                  const value = Number(e.target.value);
                  if (e.target.value !== "" && Number.isFinite(value)) {
                    onUpdate({ params: { [param.key]: value } });
                  }
                }}
                onBlur={(e) =>
                  onUpdate({
                    params: {
                      [param.key]: clamp(
                        Number(e.target.value),
                        param.min,
                        param.max,
                      ),
                    },
                  })
                }
              />
            </div>
          </label>
        ),
      )}
      {clip.preset === "morphToElement" && (
        <label>
          Custom shape
          <div style={{ display: "flex", gap: "8px" }}>
            <button onClick={() => setDrawFrameOpen(true)}>
              {clip.customPath ? "Edit drawn shape" : "Draw shape"}
            </button>
            {clip.customPath && (
              <button onClick={() => onUpdate({ customPath: undefined })}>
                Clear
              </button>
            )}
          </div>
        </label>
      )}
      {drawFrameOpen && supportsCustomPath(clip.preset) && (
        <div className="clip-settings__draw-frame">
          <DrawShapePad
            size={240}
            outline={customOutline}
            onDraw={(d) => onUpdate({ customPath: d })}
          />
          {clip.customPath && !customOutline && (
            <span className="animation-panel__error">
              Invalid custom path — the built-in Shape is used instead.
            </span>
          )}
          <div style={{ display: "flex", gap: "8px" }}>
            {onDrawMorphTarget && (
              <button
                onClick={() => {
                  setDrawFrameOpen(false);
                  onDrawMorphTarget();
                }}
                title="Draw the target shape on the main canvas instead — it becomes the morph target and is then removed"
              >
                Draw on canvas
              </button>
            )}
            <button onClick={() => setDrawFrameOpen(false)}>Done</button>
          </div>
        </div>
      )}
      <div className="clip-settings__footer">
        <button
          title="Restore this preset's default timing and parameters"
          onClick={() => {
            const defaults = animationPresets[clip.preset]?.defaults;
            if (!defaults) {
              return;
            }
            // authored content survives a reset — drawn paths, keyframe
            // curves, chaining and variation are not "tunables"
            onUpdate({
              duration: defaults.duration,
              delay: defaults.delay,
              repeat: defaults.repeat,
              easing: defaults.easing,
              spring: defaults.spring,
              yoyo: defaults.yoyo,
              repeatDelayMs: defaults.repeatDelayMs,
              endDelayMs: defaults.endDelayMs,
              params: { ...defaults.params },
            });
          }}
        >
          Reset to defaults
        </button>
        <button className="clip-settings__remove" onClick={onRemove}>
          Remove animation
        </button>
      </div>
    </>
  );
};

/** Freehand mini-canvas for sketching the morph target shape. The stroke is
 * serialized to SVG path data ("M… L… Z") — the exact `customPath` format the
 * morph pipeline already samples via svgPathToOutline, so drawing and (legacy)
 * template-provided paths share one storage format. */
const DrawShapePad = ({
  size,
  outline,
  onDraw,
}: {
  size: number;
  /** committed shape, normalized to a 0..1 bbox (null = none/invalid) */
  outline: number[] | null;
  onDraw: (pathData: string) => void;
}) => {
  const [stroke, setStroke] = useState<[number, number][] | null>(null);

  const localPoint = (e: ReactPointerEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return [
      Math.round(e.clientX - rect.left),
      Math.round(e.clientY - rect.top),
    ] as [number, number];
  };

  return (
    <svg
      className="clip-settings__draw-pad"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        setStroke([localPoint(e)]);
      }}
      onPointerMove={(e) => {
        if (!stroke) {
          return;
        }
        const [x, y] = localPoint(e);
        const [px, py] = stroke[stroke.length - 1];
        // thin the stroke: ≥3px between samples keeps the path string short
        if (Math.hypot(x - px, y - py) >= 3) {
          setStroke([...stroke, [x, y]]);
        }
      }}
      onPointerUp={() => {
        if (stroke && stroke.length >= 3) {
          const [first, ...rest] = stroke;
          onDraw(
            `M${first[0]} ${first[1]} ${rest
              .map(([x, y]) => `L${x} ${y}`)
              .join(" ")} Z`,
          );
        }
        setStroke(null);
      }}
    >
      {stroke ? (
        <polyline
          points={stroke.map(([x, y]) => `${x},${y}`).join(" ")}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
        />
      ) : outline ? (
        // committed shape, scaled from its normalized 0..1 bbox into the pad
        <polygon
          points={Array.from(
            { length: outline.length >> 1 },
            (_, i) =>
              `${10 + outline[i * 2] * (size - 20)},${
                10 + outline[i * 2 + 1] * (size - 20)
              }`,
          ).join(" ")}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
        />
      ) : (
        <text
          x={size / 2}
          y={size / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="currentColor"
          opacity={0.5}
          fontSize={11}
        >
          draw the target shape
        </text>
      )}
    </svg>
  );
};
